/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { FetchReportProperties } from '../../interface/reports.interface';
import reportApi from '../../utils/api/report';

interface ReportFilter {
  startDate: string;
  endDate: string;
  year: string;
}

interface ReportState {
  activePinName: string[];
  reportByType: string;
  accountId: string;
  reportFilter: ReportFilter;
  isReportUpdated: boolean;
  allBookmarks: any[];
}

interface GetBookMarkInterFace {
  entity: string;
  accountCustomId: string;
}

export const fetchBookMarksList = createAsyncThunk<FetchReportProperties, GetBookMarkInterFace>(
  'reports/fetchBookMarksList',
  async (data: GetBookMarkInterFace, { dispatch }) => {
    try {
      const response = await reportApi.getBookMark(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const defaultEndDate = dayjs(); // today
const defaultStartDate = dayjs().subtract(1, 'day');

const initialState: ReportState = {
  reportByType: 'station',
  accountId: '',
  reportFilter: {
    startDate: defaultStartDate.format('YYYY-MM-DD'),
    endDate: defaultEndDate.format('YYYY-MM-DD'),
    year: '',
  },
  isReportUpdated: false,
  activePinName: [],
  allBookmarks: [],
};

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    /**
     * Updates the report state based on the provided name and value.
     * If the name is 'reportByType', updates the reportByType field.
     * Otherwise, updates the corresponding field in reportFilter and sets isReportUpdated if applicable.
     */
    setReportsData: (
      state,
      action: PayloadAction<{
        name: keyof ReportFilter | 'reportByType';
        value: string;
      }>,
    ) => {
      const { name, value } = action.payload;

      if (name === 'reportByType') {
        return {
          ...state,
          reportByType: value,
        };
      }
      return {
        ...state,
        reportFilter: {
          ...state.reportFilter,
          [name]: value,
        },
        isReportUpdated: ['startDate', 'endDate', 'year'].includes(name),
      };
    },
    /**
     * Resets the isReportUpdated flag to false.
     */
    resetReportUpdateFlag: (state) => {
      return {
        ...state,
        isReportUpdated: false,
      };
    },
    /**
     * Sets the active pin name to the provided value.
     * @param {string} action.payload - The name of the active pin.
     */
    setActivePin: (state, action: PayloadAction<string>) => {
      const pin = action.payload;
      if (state.activePinName.includes(pin)) {
        return {
          ...state,
          activePinName: state.activePinName.filter((item) => item !== pin),
        };
      }
      return {
        ...state,
        activePinName: [...state.activePinName, pin],
      };
    },
    /**
     * Clears the clearDateRange , setting it to null.
     */
    clearDateRange: (state) => {
      return {
        ...state,
        reportByType: 'station',
        reportFilter: {
          startDate: defaultStartDate.format('YYYY-MM-DD'),
          endDate: defaultEndDate.format('YYYY-MM-DD'),
          year: '',
        },
        isReportUpdated: false,
      };
    },

    /**
     * Clears the active pin name, setting it to null.
     */
    clearActivePin: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        activePinName: state.activePinName.filter((pin) => pin !== action.payload),
      };
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookMarksList.pending, () => {})
      .addCase(fetchBookMarksList.fulfilled, (state, action) => {
        state.allBookmarks = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchBookMarksList.rejected, () => {});
  },
});

export const {
  setReportsData,
  resetReportUpdateFlag,
  setActivePin,
  clearActivePin,
  clearDateRange,
} = reportSlice.actions;
export default reportSlice.reducer;
